import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import Select from 'components/inputs/select';
import ToggleSwitch from 'components/inputs/toggle-switch';
import AccordionSection from 'components/layout/accordion-section';
import InfoTooltip from 'components/layout/info-tooltip';
import { MatchingAirHandlersModeType } from 'data/types';
import {
  defaultPtacSelectionFilters,
  setPtacSelectionFilters
} from 'features/ptac-selection';
import {
  clearDuctedFilters,
  setGalleryFilters,
  setMatchingAirHandlersMode,
  updateEquipmentFilter,
  updateGalleryFilter
} from 'features/ui';
import useFilterTooltips from 'hooks/use-filter-tooltips';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import { everyPositionedCondenserHasAirHandler } from 'utils/configurations';
import { hasPtacFilter } from 'utils/ptac-system-filters';
import { defaultGalleryFilters } from 'utils/system-gallery-filters';
import DuctedSystemFiltersSection from './ducted-system-filters-section';
import EquipmentFiltersControls from './equipment-filters-controls';
import PtacSystemFiltersSection from './ptac-system-filters-section';

const MATCHING_MODE_OPTIONS: ReadonlyArray<{
  value: MatchingAirHandlersModeType;
  label: string;
}> = [
  { value: 'auto', label: 'Preferred' },
  { value: 'required', label: 'Required' },
  { value: 'off', label: 'Scoring Based' }
];

export default function SystemFiltersPanel() {
  const dispatch = useAppDispatch();
  const galleryFilters = useAppSelector((state) => state.ui.galleryFilters);
  const equipmentFilters = useAppSelector((state) => state.ui.equipmentFilters);
  const matchingAirHandlersMode = useAppSelector(
    (state) => state.ui.matchingAirHandlersMode
  );
  const activeConfiguration = useAppSelector(
    (state) =>
      state.configurations.configurations[state.configurations.activeConfig]
  );
  const requiredModeAvailable =
    everyPositionedCondenserHasAirHandler(activeConfiguration);
  // Ducted-path gating (AHA-6681 CS-1): the section render, the ducted
  // isModified/reset participation, and the fetches (gated inside the page
  // hook) all key off this — ductless behavior stays byte-identical.
  const isDucted = useIsCompatibilityPathway();
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  // PTAC-path gating: the brand/series systems have no air handlers,
  // condenser series or refrigerants to filter on, so the equipment
  // dimensions give way to the PTAC section (unit type, brand, series).
  const isPtac = useIsPtacPathway();
  const ptacFilters = useAppSelector((state) => state.ptacSelection.filters);
  // Admin-managed info tooltips (AHA-6681): code → content, empty off the
  // ducted path (the hook skips the fetch there) and for codes without
  // content — no icon in either case.
  const filterTooltips = useFilterTooltips();

  const handleReset = () => {
    dispatch(setGalleryFilters(defaultGalleryFilters));
    dispatch(setMatchingAirHandlersMode('auto'));
    dispatch(
      updateEquipmentFilter({
        airHandlerTypes: null,
        condenserSeries: null,
        condenserBtus: null,
        hyperHeat: null,
        systemTypes: null,
        refrigerants: null
      })
    );
    if (isDucted) {
      dispatch(clearDuctedFilters());
    }
    if (isPtac) {
      dispatch(setPtacSelectionFilters(defaultPtacSelectionFilters));
    }
  };

  const hasEquipmentDimensionFilter =
    equipmentFilters.airHandlerTypes !== null ||
    equipmentFilters.condenserSeries !== null ||
    equipmentFilters.condenserBtus !== null ||
    equipmentFilters.hyperHeat !== null ||
    // On the ducted path the legacy systemTypes dimension is inert (hidden
    // and dropped from requests — AHA-6681 QA F6), so a persisted subset
    // must not summon the Reset button there. handleReset still nulls it.
    (!isDucted && equipmentFilters.systemTypes !== null) ||
    equipmentFilters.refrigerants !== null;

  const hasDuctedFilter =
    isDucted &&
    (ductedFilters.filters.length > 0 ||
      ductedFilters.systemTypeSelections.length > 0);

  const isModified =
    // The ducted compatibility fetch never sends matching mode, so the
    // control is hidden there and a persisted non-default mode must not
    // summon the Reset button (same rule as the inert systemTypes above).
    (!isDucted && !isPtac && matchingAirHandlersMode !== 'auto') ||
    (!isPtac && hasEquipmentDimensionFilter) ||
    hasDuctedFilter ||
    (isPtac && hasPtacFilter(ptacFilters)) ||
    galleryFilters.inStock !== defaultGalleryFilters.inStock ||
    galleryFilters.onSale !== defaultGalleryFilters.onSale ||
    galleryFilters.priceMin !== defaultGalleryFilters.priceMin ||
    galleryFilters.priceMax !== defaultGalleryFilters.priceMax;

  const matchingBadge = matchingAirHandlersMode !== 'auto' ? 1 : null;
  const priceBadge =
    (galleryFilters.priceMin !== null ? 1 : 0) +
      (galleryFilters.priceMax !== null ? 1 : 0) || null;

  return (
    <div
      id='system-filters-panel'
      className='flex flex-col gap-4 rounded-lg border border-gray-200 bg-white px-4 pb-1 pt-4 shadow-sm'
    >
      <div className='text-xl font-bold text-gray-900'>System Filters</div>

      <div className='flex items-center justify-between gap-2'>
        <span
          className={clsx(
            'text-sm font-medium text-gray-700',
            // Flex only when the icon renders — the ductless path (no
            // tooltips fetched) keeps its exact pre-feature DOM.
            filterTooltips.inStock && 'flex items-center gap-1.5'
          )}
        >
          In stock only
          {filterTooltips.inStock && (
            <InfoTooltip>{filterTooltips.inStock}</InfoTooltip>
          )}
        </span>
        <ToggleSwitch
          value={galleryFilters.inStock}
          onChange={(v) => dispatch(updateGalleryFilter({ inStock: v }))}
        />
      </div>

      <div className='flex flex-col border-t border-gray-100'>
        {!isDucted && !isPtac && (
          <AccordionSection title='Matching Air Handlers' badge={matchingBadge}>
            <Select
              enableChips
              fullWidth
              preventDeselection
              value={matchingAirHandlersMode}
              onChange={(value) =>
                dispatch(
                  setMatchingAirHandlersMode(
                    value as MatchingAirHandlersModeType
                  )
                )
              }
              options={MATCHING_MODE_OPTIONS.map((option) => ({
                ...option,
                disabled: option.value === 'required' && !requiredModeAvailable
              }))}
            />
          </AccordionSection>
        )}
        <AccordionSection
          title='Price Range'
          badge={priceBadge}
          info={filterTooltips.priceRange}
        >
          <div className='flex items-center gap-1.5'>
            <input
              type='number'
              placeholder='Min'
              value={galleryFilters.priceMin ?? ''}
              onChange={(e) =>
                dispatch(
                  updateGalleryFilter({
                    priceMin: e.target.value ? Number(e.target.value) : null
                  })
                )
              }
              className='w-full min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-sm'
            />
            <span className='shrink-0 text-gray-400'>-</span>
            <input
              type='number'
              placeholder='Max'
              value={galleryFilters.priceMax ?? ''}
              onChange={(e) =>
                dispatch(
                  updateGalleryFilter({
                    priceMax: e.target.value ? Number(e.target.value) : null
                  })
                )
              }
              className='w-full min-w-0 rounded-md border border-gray-300 px-2 py-1.5 text-sm'
            />
          </div>
        </AccordionSection>
        {isPtac ? (
          <PtacSystemFiltersSection />
        ) : (
          <EquipmentFiltersControls hideSystemTypeSection={isDucted} />
        )}
        {isDucted && <DuctedSystemFiltersSection />}
      </div>

      {isModified && (
        <button
          type='button'
          onClick={handleReset}
          className='-mt-2 mb-2 self-start text-sm font-medium text-blue-600 hover:text-blue-800'
        >
          Reset Filters
        </button>
      )}
    </div>
  );
}
